/**
 * Mock Data Constants for Dashboard Components
 * 
 * IMPORTANT: This file contains ALL placeholder data used in the dashboard.
 * 
 * Purpose:
 * - Centralize all mock/placeholder data in one location
 * - Avoid hardcoding data directly in components
 * - Easy to identify and replace when connecting to backend API
 * - Clear separation between UI logic and data
 * 
 * When to Update:
 * - When backend API is ready, replace these with API calls
 * - When testing different scenarios, modify values here
 * - When adding new dashboard features, add mock data here first
 * 
 * Related Rule: AGENT_MASTER_OPERATING_RULES.md - Section 7.5
 * 
 * Created: 2025-01-16 (Dashboard Redesign Phase 2)
 * Last Modified: 2025-01-16
 */

// ============================================
// TAX SAVINGS SUMMARY MOCK DATA
// ============================================
export const MOCK_TAX_SAVINGS_SUMMARY = {
  totalSavings: 45650,      // Total tax savings year-to-date (USD)
  roi: '12.8x',             // Return on investment multiplier
  investment: 3200,         // Annual subscription cost (USD)
};

// Future: Replace with API call
// import { getTaxSavingsSummary } from '@/services/api';
// const data = await getTaxSavingsSummary();

// ============================================
// IMPLEMENTATION PROGRESS MOCK DATA
// ============================================
export const MOCK_IMPLEMENTATION_PROGRESS = {
  overallProgress: 38,      // Overall completion percentage (0-100)
  strategies: [
    {
      id: 1,
      name: 'S-Corporation Election',
      status: 'completed',
      savings: 8500,
    },
    {
      id: 2,
      name: 'Home Office Deduction',
      status: 'completed',
      savings: 3200,
    },
    {
      id: 3,
      name: 'Business Equipment Purchase',
      status: 'in_progress',
      progress: 75,
      savings: 12000,
    },
  ],
};

// Future: Replace with API call
// import { getImplementationProgress } from '@/services/api';
// const data = await getImplementationProgress();

// ============================================
// QUICK WINS MOCK DATA
// ============================================
export const MOCK_QUICK_WINS = {
  actions: [
    { id: 1, text: 'Set up business bank account', completed: true },
    { id: 2, text: 'Install expense-tracking app', completed: true },
    { id: 3, text: 'Organize receipts folder', completed: true },
    { id: 4, text: 'Schedule quarterly tax payments', completed: false },
    { id: 5, text: 'Review business credit cards', completed: false },
    { id: 6, text: 'Update mileage log system', completed: false },
    { id: 7, text: 'Document home office setup', completed: false },
  ],
};

// Future: Replace with API call
// import { getQuickWins } from '@/services/api';
// const data = await getQuickWins();

// ============================================
// TAX CALENDAR MOCK DATA
// ============================================
export const MOCK_TAX_CALENDAR = {
  deadlines: [
    {
      id: 1,
      title: 'Q1 Estimated Taxes',
      dueDate: '2025-01-15',
      type: 'payment',
    },
    {
      id: 2,
      title: 'Annual Tax Return Filing',
      dueDate: '2025-04-15',
      type: 'filing',
    },
    {
      id: 3,
      title: 'Q2 Estimated Taxes',
      dueDate: '2025-06-17',
      type: 'payment',
    },
  ],
};

// Future: Replace with API call
// import { getTaxCalendar } from '@/services/api';
// const data = await getTaxCalendar();

// ============================================
// MOCK DATA REGISTRY
// ============================================
// This object tracks all mock data used in the application
// Update this when adding new mock data sources

export const MOCK_DATA_REGISTRY = {
  'TaxSavingsSummary': {
    constant: 'MOCK_TAX_SAVINGS_SUMMARY',
    fields: ['totalSavings', 'roi', 'investment'],
    location: 'FreeDashboard.js',
    futureAPI: 'getTaxSavingsSummary()',
  },
  'ImplementationProgress': {
    constant: 'MOCK_IMPLEMENTATION_PROGRESS',
    fields: ['overallProgress', 'strategies'],
    location: 'FreeDashboard.js',
    futureAPI: 'getImplementationProgress()',
  },
  'QuickWins': {
    constant: 'MOCK_QUICK_WINS',
    fields: ['actions'],
    location: 'FreeDashboard.js',
    futureAPI: 'getQuickWins()',
  },
  'TaxCalendar': {
    constant: 'MOCK_TAX_CALENDAR',
    fields: ['deadlines'],
    location: 'FreeDashboard.js',
    futureAPI: 'getTaxCalendar()',
  },
};

// ============================================
// EXPORT ALL
// ============================================
export default {
  MOCK_TAX_SAVINGS_SUMMARY,
  MOCK_IMPLEMENTATION_PROGRESS,
  MOCK_QUICK_WINS,
  MOCK_TAX_CALENDAR,
  MOCK_DATA_REGISTRY,
};
